import { useState, useEffect } from 'react';
import { useLang } from '../../../i18n/LangContext';
import { langNames, langFlags, Lang } from '../../../i18n/translations';

interface NavigationProps {
  activeSection: string;
}

const LANGS: Lang[] = ['bg', 'en', 'it', 'ar', 'zh', 'ru'];

export default function Navigation({ activeSection }: NavigationProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const navItems = [
    { id: 'hero',         label: t('nav_home') },
    { id: 'about',        label: t('nav_about') },
    { id: 'mission',      label: t('nav_mission') },
    { id: 'experience',   label: t('nav_experience') },
    { id: 'testimonials', label: t('nav_testimonials') },
    { id: 'clients',      label: t('nav_clients') },
    { id: 'contact',      label: t('nav_contact') },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-[#0a0e1a]/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection('hero')}>
            <img
              src="https://static.readdy.ai/image/5490f3a4ad0e9eb55a0769efce385370/59278221c6b2727f8d1b3dc0934be840.jpeg"
              alt="Алекс Зингер"
              className="w-12 h-12 rounded-full object-cover border-2 border-[#d4af37]"
            />
            <div>
              <div className="text-lg font-semibold text-[#d4af37]">{t('footer_title')}</div>
              <div className="text-xs text-gray-400">{t('nav_subtitle')}</div>
            </div>
          </div>

          {/* Nav links */}
          <ul className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium transition-all duration-300 hover:text-[#d4af37] whitespace-nowrap cursor-pointer ${
                    activeSection === item.id ? 'text-[#d4af37]' : 'text-gray-300'
                  }`}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>

          {/* Right side: lang switcher + CTA */}
          <div className="flex items-center gap-3">
            {/* Language switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#d4af37]/30 bg-[#1a1f35]/60 hover:border-[#d4af37]/70 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <span className="text-base leading-none">{langFlags[lang]}</span>
                <span className="text-[#d4af37] text-xs font-bold tracking-wide">{langNames[lang]}</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-[#d4af37] text-sm transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {langOpen && (
                <div className="absolute right-0 top-full mt-2 bg-[#1a1f35] border border-[#d4af37]/30 rounded-xl shadow-2xl shadow-black/50 overflow-hidden z-50 min-w-[120px]">
                  {LANGS.map((l) => (
                    <button
                      key={l}
                      onClick={() => { setLang(l); setLangOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all duration-150 cursor-pointer whitespace-nowrap hover:bg-[#d4af37]/10 ${
                        lang === l ? 'text-[#d4af37] bg-[#d4af37]/10' : 'text-gray-300'
                      }`}
                    >
                      <span className="text-base">{langFlags[l]}</span>
                      <span className="font-medium">{langNames[l]}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CTA button */}
            <button
              onClick={() => scrollToSection('contact')}
              className="hidden md:block px-5 py-2.5 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-[#0a0e1a] font-semibold rounded-lg hover:shadow-lg hover:shadow-[#d4af37]/50 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
            >
              {t('nav_cta')}
            </button>
          </div>
        </div>
      </div>

      {/* Close dropdown on outside click */}
      {langOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
      )}
    </nav>
  );
}
